// ─────────────────────────────────────────────────────────────────────────────
// siteContent.ts — single source of truth for all site text and UI content.
//
// To update any text on the site, edit this file only.
// Project data lives in projectsData.ts.
// ─────────────────────────────────────────────────────────────────────────────

export const siteContent = {
    // ── Brand ──────────────────────────────────────────────────────────────────
    site: {
        name: "Betamax Aluminum",
        logo: {
            dark: "/images/ui/logo.png",
            white: "/images/ui/logo-white.png",
        },
    },

    // ── Navigation ─────────────────────────────────────────────────────────────
    nav: {
        links: [
            { label: "Projects", to: "/projects" },
            { label: "Solutions", to: "/solutions" },
            { label: "About Us", to: "/about" },
        ],
        cta: { label: "Contact Us", to: "/contact" },
    },

    // ── Footer ─────────────────────────────────────────────────────────────────
    footer: {
        address: {
            company: "BetaMax Aluminum",
            street: "Sheraton, Cairo",
            city: "",
        },
        phone: {
            display: "(+20)106 500 7010",
            href: "tel:+201065007010",
        },
        email: {
            display: "info@betamax-alu.com",
            href: "mailto:info@betamax-alu.com",
        },
        linkedin: "https://www.linkedin.com/company/betamax-aluminum",
        copyright: "Betamax Aluminum. All Rights Reserved.",
        nav: [
            { label: "Projects", to: "/projects" },
            { label: "Solutions", to: "/solutions" },
            { label: "About Us", to: "/about" },
            // { label: "Contact", to: "/contact" },
        ],
    },

    // ── Home page ──────────────────────────────────────────────────────────────
    homepage: {
        hero: {
            title: "We Transform Buildings with Premium Aluminum Solutions",
            image: "/images/hero/main-background.jpg",
        },
        features: [
            {
                title: "Architectural Aluminum Systems",
                description:
                    "From curtain walls to composite panels and skylights, we deliver precision-engineered aluminum solutions for any building type. Explore how we can elevate your next project.",
                linkTo: "/solutions",
                mainImage: "/images/hero/main-background.jpg",
                floatingImage: "/images/hero/main-background.jpg",
                reverse: false,
            },
            {
                title: "Built on Trust",
                description:
                    "Betamax Aluminum partners with architects, contractors, and developers to deliver premium facade systems with integrity and precision. Get to know the team behind the work.",
                linkTo: "/about",
                mainImage: "/images/hero/main-background.jpg",
                floatingImage: "/images/hero/main-background.jpg",
                reverse: true,
            },
        ],
        projectsSection: {
            label: "PROJECTS",
            title: "What We Do",
            linkTo: "/projects",
            linkLabel: "View All Projects",
        },
    },

    // ── Projects page ─────────────────────────────────────────────────────────
    projectsPage: {
        title: "What We Do",
        description:
            "Our projects display a wide range of aluminum systems — from curtain walls and ACP cladding to windows, doors, and skylights — that transform buildings across Egypt and beyond.",
    },

    // ── Contact page ───────────────────────────────────────────────────────────
    contactPage: {
        pageTitle: "Contact",
        heading: "Let's Build What's Next. Together.",
        description:
            "Have a vision taking shape? Exploring a design challenge or facade concept? Our engineering and design teams are here to collaborate with you from early sketches to technical details.",
        callToAction: "Share a few details below or call",
        phone: {
            display: "+1 (212) 000-0000",
            href: "tel:+12120000000",
        },
        image: "/images/hero/main-background.jpg",
        projectTypes: [
            "New Construction",
            "Renovation",
            "Facade Systems",
            "Curtain Wall",
            "Other",
        ],
    },

    // ── Solutions page ─────────────────────────────────────────────────────────
    solutionsPage: {
        pageTitle: "Solutions",
        hero: {
            title: "Comprehensive Aluminum Solutions",
            subtitle:
                "From curtain walls to skylights, we deliver innovative systems that meet your architectural vision",
            image: "/images/hero/main-background.jpg",
        },
        services: [
            {
                slug: "curtain-walls",
                title: "Curtain Walls",
                description:
                    "Our curtain wall systems provide exceptional structural performance while maximizing natural light and offering stunning aesthetic appeal. Engineered for durability and energy efficiency, our solutions seamlessly integrate with any architectural design.",
                mainImage: "/images/hero/main-background.jpg",
                floatingImage: "/images/hero/main-background.jpg",
            },
            {
                slug: "doors",
                title: "Aluminum Doors",
                description:
                    "Premium aluminum door systems combining security, functionality, and elegant design. From entrance doors to sliding systems, our products deliver reliable performance with minimal maintenance requirements and maximum longevity.",
                mainImage: "/images/solutions/doors/doors_closed.JPG",
                floatingImage: "/images/solutions/doors/doors_opened.JPG",
            },
            {
                slug: "windows",
                title: "Windows",
                description:
                    "High-performance aluminum window systems engineered for superior thermal insulation, sound dampening, and weather resistance. Our windows combine sleek modern aesthetics with exceptional functionality for residential and commercial applications.",
                mainImage: "/images/project/eden-rehab-club/window.jpg",
                floatingImage: "/images/project/eden-rehab-club/window.jpg",
            },
            {
                slug: "acp",
                title: "Aluminum Composite Panel",
                description:
                    "Versatile ACP cladding solutions that transform building exteriors with contemporary style and outstanding durability. Our composite panels offer superior flatness, color consistency, and weather resistance while providing excellent design flexibility.",
                mainImage: "/images/hero/main-background.jpg",
                floatingImage: "/images/hero/main-background.jpg",
            },
            {
                slug: "louvers",
                title: "Louvers",
                description:
                    "Architectural louver systems designed to control light, airflow, and privacy while enhancing building aesthetics. Our precision-engineered louvers provide optimal ventilation and sun shading with customizable configurations for any project requirement.",
                mainImage: "/images/hero/main-background.jpg",
                floatingImage: "/images/hero/main-background.jpg",
            },
            {
                slug: "sky-lights",
                title: "Sky Lights",
                description:
                    "Innovative skylight systems that flood interiors with natural daylight while maintaining thermal efficiency. Our engineered solutions feature robust aluminum framing, weather-tight seals, and options for ventilation and solar control.",
                mainImage: "/images/hero/main-background.jpg",
                floatingImage: "/images/hero/main-background.jpg",
            },
        ],
        process: {
            label: "OUR PROCESS",
            title: "How We Work",
            steps: [
                {
                    number: "01",
                    title: "Design & Planning",
                    items: [
                        "Gather client requirements and project vision",
                        "Prepare drawings and materials list",
                        "Confirm design aligns with project requirements",
                    ],
                },
                {
                    number: "02",
                    title: "Fabrication",
                    items: [
                        "Order materials based on project specs",
                        "Fabricate all items internally with quality assurance",
                        "Prepare units for site delivery",
                    ],
                },
                {
                    number: "03",
                    title: "Installation",
                    items: [
                        "Get site ready and assign team",
                        "Install aluminum systems",
                        "Conduct final inspection and hand over completed works",
                    ],
                },
            ],
        },
    },

    // ── About page ─────────────────────────────────────────────────────────────
    aboutPage: {
        pageTitle: "About Us",
        hero: {
            title: "Building Excellence Through Innovation",

            image: "/images/hero/main-background.jpg",
        },
        mission: {
            label: "WHO WE ARE",
            title: "Fabrication & Installation of Architectural Aluminum Systems",
            description:
                "BetaMax Aluminum specializes in fabricating and installing architectural aluminum systems — curtain walls, ACP, louvers, windows, doors, and skylights. We provide tailored, high-quality solutions with precision, speed, and professionalism to meet modern construction demands.",
        },
        expertise: {
            label: "EXPERTISE",
            title: "What Sets Us Apart",
            items: [
                {
                    title: "Design Excellence",
                    description:
                        "Our team of expert engineers and designers work closely with you from concept to completion, ensuring every detail meets your specifications.",
                },
                {
                    title: "Quality Craftsmanship",
                    description:
                        "Using only premium materials and state-of-the-art fabrication techniques, we deliver systems built to last.",
                },
                {
                    title: "Proven Track Record",
                    description:
                        "From commercial towers to residential complexes, our portfolio spans diverse projects across New York and North America.",
                },
                {
                    title: "Full-Service Solutions",
                    description:
                        "From initial design consultation to final installation, we provide comprehensive support throughout your project lifecycle.",
                },
            ],
        },
        values: {
            label: "OUR VALUES",
            title: "Built on Trust and Innovation",
            items: [
                {
                    title: "Integrity",
                    description:
                        "We stand behind our work with transparent communication and honest partnerships.",
                },
                {
                    title: "Innovation",
                    description:
                        "Continuously evolving our techniques and systems to meet modern architectural demands.",
                },
                {
                    title: "Sustainability",
                    description:
                        "Committed to eco-friendly practices and energy-efficient solutions that benefit our communities.",
                },
            ],
        },
        cta: {
            title: "Ready to Start Your Project?",
            description:
                "Let's discuss how Betamax Aluminum can bring your architectural vision to life.",
            buttonLabel: "Contact Us",
            buttonLink: "/contact",
        },
    },
} as const;
